import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { download } from '@src/utils/download-utils';
import { Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { EmptyState } from '@Components/EmptyState';
import { EntityKind, EntityLogo } from '@Components/EntityLogo';
import { DEFAULT_HEAT_MAPPING, getHeatColumn, HeatCell } from '@Components/MetricsTable/HeatCell';
import { hasNumber } from '@Components/MetricsTable/metrics-table-utils';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Spinner } from '@Components/Spinner';
import { BPMTable, TypedHeader } from '@Components/StickyTable/BPMTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import {
  DEVOSCAT_IMAGES,
  DisplayRow,
  PLATFORMS,
  PlatformType,
  StageTwoRow
} from './streaming-ml-diagnostics-constants';
import {
  buildCsv,
  formatNumber,
  getNetwork,
  toDisplayRows
} from './streaming-ml-diagnostics-utils';
import { useStageTwoDiagnostics } from './use-streaming-ml-diagnostics';
import './StreamingStageTwoDiagnostics.scss';

const COLUMN_WIDTH = 150;
const CSV_MIME_TYPE = 'text/csv';

type MetricKey = 'pred_cr_per_m' | 'pred_cost_per_conversion' | 'pred_cr_per_dollar';

interface MetricConfig {
  key: MetricKey;
  label: string;
  minIsBest?: boolean;
  prefix?: string;
}

const METRICS: MetricConfig[] = [
  {
    key: 'pred_cr_per_m',
    label: 'Predicted Conversion Rate per 1000 Imps'
  },
  {
    key: 'pred_cost_per_conversion',
    label: 'Predicted Cost per Conversion',
    minIsBest: true,
    prefix: '$'
  },
  {
    key: 'pred_cr_per_dollar',
    label: 'Predicted Conversion Rate per 1000 Dollars'
  }
];

const metricLabel = (val: number | null | undefined, prefix = ''): string =>
  hasNumber(val) ? `${prefix}${formatNumber(val)}` : '-';

const metricHeader = (
  rows: DisplayRow[],
  { key, label, minIsBest, prefix }: MetricConfig
): TypedHeader<DisplayRow> => {
  const heatColumn = getHeatColumn(
    rows.map(row => row[key]),
    rows.map(row => metricLabel(row[key], prefix)),
    { ...DEFAULT_HEAT_MAPPING, minIsBest }
  );
  return {
    label,
    name: key,
    width: COLUMN_WIDTH,
    nonFilterable: true,
    contentGetter: row => row[key] ?? 0,
    renderer: row => (
      <HeatCell
        label={metricLabel(row[key], prefix)}
        value={row[key]}
        column={heatColumn}
        // Only reachable with "Include unseen" on.
        overlayText={row.is_seen === 't' ? undefined : 'Not seen in Stage One'}
      />
    )
  };
};

const buildHeaders = (rows: DisplayRow[], showDevOSCat: boolean): TypedHeader<DisplayRow>[] => {
  const cpmColumn = getHeatColumn(
    [],
    rows.map(row => metricLabel(row.cpm, '$'))
  );
  return [
    {
      label: 'Placement',
      name: 'display_name',
      flex: 2,
      minFlexWidth: 320,
      renderer: row => <PlacementCell row={row} />
    },
    ...(showDevOSCat
      ? [
          {
            label: 'DevOSCat',
            name: 'devcatos',
            width: COLUMN_WIDTH,
            renderer: (row: DisplayRow) => <DevOSCatCell devcatos={row.devcatos ?? ''} />
          }
        ]
      : []),
    metricHeader(rows, METRICS[0]),
    {
      label: 'CPM',
      name: 'cpm',
      width: COLUMN_WIDTH,
      nonFilterable: true,
      contentGetter: row => row.cpm ?? 0,
      // Not heat-mapped (no value passed), but laid out like the heat cells beside it.
      renderer: row => (
        <HeatCell label={metricLabel(row.cpm, '$')} value={null} column={cpmColumn} />
      )
    },
    metricHeader(rows, METRICS[1]),
    metricHeader(rows, METRICS[2])
  ];
};

interface StreamingStageTwoDiagnosticsProps {
  company: string;
  platform: PlatformType;
  setPlatform: Dispatch<SetStateAction<PlatformType>>;
  showDevOSCat: boolean;
  setShowDevOSCat: Dispatch<SetStateAction<boolean>>;
  showUnseen: boolean;
  setShowUnseen: Dispatch<SetStateAction<boolean>>;
}

export const StreamingStageTwoDiagnostics = ({
  company,
  platform,
  setPlatform,
  showDevOSCat,
  setShowDevOSCat,
  showUnseen,
  setShowUnseen
}: StreamingStageTwoDiagnosticsProps) => {
  const { data, isError } = useStageTwoDiagnostics(company, platform, showDevOSCat);
  const [filteredRows, setFilteredRows] = useState<StageTwoRow[]>([]);

  const rows = useMemo(
    () => (data?.rows ? toDisplayRows(data.rows, showUnseen) : undefined),
    [data, showUnseen]
  );
  const headers = useMemo(() => buildHeaders(rows ?? [], showDevOSCat), [rows, showDevOSCat]);

  // Always render the table so its control strip stays usable while loading or empty.
  const renderNoRows = () => {
    // The page shows (and reports) the error in a modal; keep this one neutral so it isn't shown twice.
    if (isError) {
      return <TableEmptyState message="Couldn't load Stage Two data." />;
    }
    if (!data) {
      return <Spinner />;
    }
    if (!rows?.length) {
      return (
        <EmptyState
          title='No Stage Two data'
          description={`Stage Two hasn't produced ${platform.toLowerCase()} output for this client yet.`}
        />
      );
    }
    return <TableEmptyState message='No placements to show' />;
  };

  const controls = (
    <div className='stage-two-controls'>
      <SegmentedControlButton
        size={Size.LG}
        isExclusiveSelect
        options={PLATFORMS}
        selectedOption={platform}
        onChange={setPlatform}
      />
      <ToggleSwitch
        size={Size.LG}
        label='Split by DevOSCat'
        checked={showDevOSCat}
        onChange={setShowDevOSCat}
        data-testid='devoscat-toggle'
      />
      <ToggleSwitch
        size={Size.LG}
        label='Include unseen'
        checked={showUnseen}
        onChange={setShowUnseen}
        infoTooltipText='Include placements that were not seen in Stage One.'
        data-testid='unseen-toggle'
      />
      <DownloadDropdown
        size={Size.LG}
        onClickOptions={[
          () => download(buildCsv(filteredRows, showDevOSCat), 'export.csv', CSV_MIME_TYPE)
        ]}
        menuOptions={['CSV']}
        disabled={filteredRows.length === 0}
      />
    </div>
  );

  return (
    <TablePage className='stage-two-diagnostics' dataTestId='stage-two-table'>
      <BPMTable
        // Remount on a new query so the filter bar reports the new rows for the CSV export.
        key={`${platform}_${showDevOSCat}_${showUnseen}`}
        alternateColors
        noRowsRenderer={renderNoRows}
        data={rows ?? []}
        headers={headers}
        onFilteredDataChange={setFilteredRows}
        additionalControls={controls}
      />
    </TablePage>
  );
};

interface PlacementCellProps {
  row: DisplayRow;
}

const PlacementCell = ({ row }: PlacementCellProps) => {
  const network = getNetwork(row.placement);
  return (
    <OverlayTrigger placement='right' overlay={<Tooltip>{network}</Tooltip>}>
      <div className='stage-two-logo-cell'>
        <EntityLogo kind={EntityKind.NETWORK} id={network} size={Size.MD} />
        {row.display_name}
      </div>
    </OverlayTrigger>
  );
};

interface DevOSCatCellProps {
  devcatos: string;
}

const DevOSCatCell = ({ devcatos }: DevOSCatCellProps) => {
  const label = devcatos.toUpperCase();
  return (
    <div className='stage-two-logo-cell'>
      <EntityLogo kind={EntityKind.NETWORK} id={DEVOSCAT_IMAGES[label] ?? label} size={Size.MD} />
      {label}
    </div>
  );
};
